import React, { useMemo } from 'react';
import styled from 'styled-components';

import type { IAgent } from '@/store/types';
import type { IAgentsTableConfig } from '@/components/client/contracts';

import { Table } from '@/components/common';
import { formatDate } from '@/helpers';
import { MONTH_DAY_YEAR_FORMAT } from '@/config/variables';
import { ChevronRightAlternative } from '@/assets/icons';
import { media } from '@/styles/theme';
import { colors, otherColors } from '@/styles/colors';
import { getDateFromBlock } from '@/helpers/block';
import { useTimezone } from '@/hooks';

interface IProps {
  agent: IAgent;
  onRowSelect: (userId: number) => void;
  tableConfig: IAgentsTableConfig;
}

const AgentRow = ({ agent, onRowSelect, tableConfig }: IProps): React.ReactElement => {
  const { timezoneName } = useTimezone();

  const contractAssnStartDate = useMemo<Date | null>(
    () => getDateFromBlock(agent.contractAssnStartBlock, timezoneName),
    [agent.contractAssnStartBlock, timezoneName],
  );

  return (
    <Table.TableRow
      key={agent.userId}
      className="small row-pointer"
      firstBgColor={colors.gray.c50}
      secondBgColor={otherColors.white}
      onClick={() => onRowSelect(agent.userId)}
    >
      <FirstNameCell width={tableConfig.labelFirstName.width}>{agent.firstName}</FirstNameCell>
      <Table.Cell width={tableConfig.labelLastName.width}>{agent.lastName}</Table.Cell>
      <Table.Cell className="hide-on-small-screen" width={tableConfig.labelStartDate.width}>
        {contractAssnStartDate && formatDate(contractAssnStartDate, MONTH_DAY_YEAR_FORMAT)}
      </Table.Cell>
      <ButtonCell width={tableConfig.button.width}>
        <ArrowRightIcon />
      </ButtonCell>
    </Table.TableRow>
  );
};

export { AgentRow };

const ArrowRightIcon = styled.div`
  width: 35px;
  height: 30px;
  mask-image: url(${ChevronRightAlternative});
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 7px 12px;
  background-color: #000;
  cursor: pointer;
`;
const FirstNameCell = styled(Table.Cell)`
  ${media.laptop} {
    margin-left: 22px;
  }
`;

const ButtonCell = styled(Table.Cell)<{ width?: string }>`
  display: flex;
  flex: 1;
  justify-content: flex-end;

  ${media.laptop} {
    display: initial;
    flex: initial;
    justify-content: initial;
  }
`;
